import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from 'store';
import { adminRolesActions } from 'features/admin-roles/redux/admin-roles.actions';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loader from 'shared/components/loader';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { ROUTES } from 'routes/constants';
import RolesForm from 'features/admin-roles/components/roles-form';
import { adminPermissionsActions } from 'features/admin-permissions/redux/admin-permissions.actions';
import { CreateRoleRequest } from 'features/admin-roles/api/types';

const UpdateAdminRolePage: React.FC = () => {
  const { items: permissions, loading: rolesLoading } = useSelector(
    (state: StoreState) => state.adminPermissions
  );
  const { isSubmitted, loading, selectedRole } = useSelector(
    (state: StoreState) => state.adminRoles
  );

  const { partner } = useSelector((state: StoreState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { roleId } = useParams();

  useEffect(() => {
    dispatch(
      adminRolesActions.getRoleRequest({
        roleId: roleId as unknown as number,
      })
    );

    dispatch(
      adminPermissionsActions.getPermissionsRequest({
        partnerName: partner as string,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - run only on mount

  const handleSubmit = (
    values: CreateRoleRequest & { permissions: string[] }
  ) => {
    const permissions_ids = permissions
      .filter((permission) => {
        if (values.permissions.includes(permission.name)) {
          return permission.id;
        }
      })
      .map((permission) => permission.id) as number[];
    dispatch(
      adminRolesActions.updateRoleRequest({
        ...values,
        permissions_ids,
        roleId: roleId as unknown as number,
      })
    );
  };

  useEffect(() => {
    if (isSubmitted) {
      navigate(ROUTES.adminRoles);
    }
  }, [isSubmitted, navigate]);

  useEffect(() => {
    return () => {
      dispatch(adminRolesActions.clearAdminRolesList());
      dispatch(adminPermissionsActions.clearState());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - cleanup only on unmount

  return (
    <Box padding="20px">
      <Breadcrumbs
        aria-label="breadcrumb"
        separator={<NavigateNextIcon fontSize="small" />}
      >
        <Link style={{ textDecoration: 'none' }} to={ROUTES.adminRoles}>
          <Typography color={'text.primary'} variant="body1">
            Roles
          </Typography>
        </Link>

        <Link
          style={{ textDecoration: 'none' }}
          to={ROUTES.dynamic.updateAdminRole(roleId)}
        >
          <Typography color={'text.secondary'} variant="body1">
            {selectedRole?.name || 'Update Role'}
          </Typography>
        </Link>
      </Breadcrumbs>

      {loading && rolesLoading && (
        <Box
          width={'100%'}
          height={'100%'}
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          position={'absolute'}
        >
          <Loader />
        </Box>
      )}

      {selectedRole?.id && (
        <RolesForm
          handleSubmit={handleSubmit}
          permissions={permissions}
          initialRole={selectedRole}
          action={'UPDATE'}
        />
      )}
    </Box>
  );
};

export default UpdateAdminRolePage;
